"use client";

import { cn } from "@/lib/utils";

export type BillingCadence = "monthly" | "annual";

interface BillingToggleProps {
  billing: BillingCadence;
  onChange: (billing: BillingCadence) => void;
  monthlyLabel: string;
  annualLabel: string;
  /**
   * @deprecated Inline % badge removed — savings now float above Annual.
   * Kept optional so existing callers still compile.
   */
  saveBadgeText?: string;
  /** Per-month price labels shown inside the toggle, e.g. "$8" / "$6" */
  monthlyPriceLabel?: string | null;
  annualPerMonthLabel?: string | null;
  /** Floating sticker above the Annual option, e.g. "Save $24/year" */
  savingsText?: string | null;
  size?: "md" | "sm";
}

export function BillingToggle({
  billing,
  onChange,
  monthlyLabel,
  annualLabel,
  monthlyPriceLabel,
  annualPerMonthLabel,
  savingsText,
  size = "md",
}: BillingToggleProps) {
  const isMd = size === "md";

  return (
    // Top padding reserves room for the floating savings sticker.
    <div className={cn("flex flex-col items-center", isMd ? "pt-4" : "pt-3.5")}>
      <div
        role="group"
        aria-label="Billing period"
        className="inline-flex items-center bg-muted rounded-full p-1 gap-1"
      >
        <button
          type="button"
          onClick={() => onChange("monthly")}
          aria-pressed={billing === "monthly"}
          className={cn(
            isMd ? "px-5 py-2 text-sm" : "px-4 py-1.5 text-xs",
            "rounded-full font-medium transition-all",
            billing === "monthly"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground"
          )}
        >
          {monthlyLabel}
          {monthlyPriceLabel && (
            <span className="ml-1.5 font-bold text-foreground/80">
              {monthlyPriceLabel}
            </span>
          )}
        </button>
        {/* Relative wrapper so the savings sticker floats above Annual. */}
        <div className="relative">
          {savingsText && (
            <span
              aria-hidden="true"
              className={cn(
                "absolute whitespace-nowrap bg-emerald-600 text-white font-extrabold rounded-full shadow-md pointer-events-none rotate-3",
                isMd
                  ? "text-[11px] px-2.5 py-0.5 -top-4 -right-5"
                  : "text-[10px] px-2 py-px -top-3.5 -right-4"
              )}
            >
              {savingsText}
            </span>
          )}
          <button
            type="button"
            onClick={() => onChange("annual")}
            aria-pressed={billing === "annual"}
            className={cn(
              isMd ? "px-5 py-2 text-sm" : "px-4 py-1.5 text-xs",
              "rounded-full font-medium transition-all flex items-center",
              isMd ? "gap-2" : "gap-1.5",
              billing === "annual"
                ? // Selected: filled pill + strong ring — clearly active.
                  "bg-background text-foreground shadow-sm ring-2 ring-emerald-500"
                : // Unselected: subtle hint only — tint + hairline ring so it
                  // reads as "best value" without looking selected.
                  "bg-emerald-500/10 text-foreground ring-1 ring-emerald-500/25 hover:bg-emerald-500/15 hover:ring-emerald-500/40"
            )}
          >
            {annualLabel}
            {annualPerMonthLabel && (
              <span className="font-bold">{annualPerMonthLabel}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
